'use client';
import React, { useEffect, useRef } from 'react';
import { useEffectDidMount } from '@benzinga/hooks';
import Modal from 'react-modal';
import { AuthenticationManager } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { logCommitHash, logFriendlyBenzingaMessageToDevelopers, logReleaseVersion } from '@benzinga/utils';
import { CoralogixRumLoader } from '@benzinga/coralogix-monitoring';
import { TrackingManager } from '@benzinga/tracking-manager';
import { WatchlistManager } from '@benzinga/watchlist-manager';

function RegisterEffects() {
  const session = React.useContext(SessionContext);
  const watchlistAddInFlightRef = useRef(false);

  useEffect(() => {
    if (document.readyState === 'complete') {
      Modal.setAppElement('#app-modal-element');
    } else {
      window.addEventListener('load', () => Modal.setAppElement('#app-modal-element'));
    }
  }, []);

  useEffectDidMount(() => {
    logCommitHash();
    logReleaseVersion();
    logFriendlyBenzingaMessageToDevelopers();
  });

  useEffectDidMount(() => {
    // Hack to prevent jumping if the last scroll position was at LazyLoaded block
    if (window.history) {
      window.history.scrollRestoration = 'manual';
    }
  });

  useEffect(() => {
    const authManager = session.getManager(AuthenticationManager);
    const subscription = authManager.subscribe(
      () => {
        CoralogixRumLoader(authManager.getAuthentication()?.user);
        subscription.unsubscribe();
      },
      undefined,
      ['authentication:session_update'],
    );

    return () => subscription.unsubscribe();
  }, [session]);

  // Listen for `bz:watchlist:add` events dispatched by the Optimizely
  // post-registration popup. Tracks the event and dispatches
  // `bz:watchlist:add:done` with { ok, error? } so the popup can confirm.
  useEffect(() => {
    const reply = (ok: boolean, error?: string) => {
      window.dispatchEvent(new CustomEvent('bz:watchlist:add:done', { detail: { ok, error } }));
    };

    const handler = async (e: Event) => {
      const detail = (
        e as CustomEvent<{
          symbols?: unknown;
          experimentId?: string;
          variationId?: string;
          source?: string;
        }>
      ).detail;
      const raw = Array.isArray(detail?.symbols) ? detail.symbols : [];
      const symbols = raw
        .map(s => (typeof s === 'string' ? s.trim().replace(/^\$/, '').toUpperCase() : ''))
        .filter(Boolean);
      const authManager = session.getManager(AuthenticationManager);
      if (symbols.length === 0) return reply(false, 'no_symbols');
      if (!authManager.isLoggedIn()) return reply(false, 'not_logged_in');
      if (watchlistAddInFlightRef.current) return reply(false, 'in_flight');

      watchlistAddInFlightRef.current = true;
      const { experimentId, variationId, source } = detail ?? {};
      const watchlistManager = session.getManager(WatchlistManager);
      const trackingManager = session.getManager(TrackingManager);

      try {
        const seedSymbols = symbols.map(symbol => ({
          countWeek: 0,
          isBZ: null,
          isBZPro: null,
          isPR: null,
          isSEC: null,
          sendRealtime: null,
          sound: null,
          status: null,
          symbol,
          symbolId: '',
          type: 'ticker',
          watchlistId: '',
        }));
        const res = await watchlistManager.createWatchlist(
          'My Watchlist',
          { emailSummary: true, realtimeBZ: false, realtimePR: false },
          seedSymbols,
        );
        if (res.err) return reply(false, 'create_failed');
        const watchlistId = res.ok.watchlistId;

        trackingManager.trackWatchlistEvent('new_watchlist', { watchlist_id: watchlistId, symbol: symbols.join(',') });

        if (experimentId) {
          trackingManager.trackCampaignEvent('click', {
            campaign_id: experimentId,
            label: 'post_reg_watchlist_save',
            partner_id: 'benzinga',
            tag: variationId,
            unit_type: source ?? 'optimizely_post_reg',
          });
        }

        return reply(true);
      } catch {
        return reply(false, 'exception');
      } finally {
        watchlistAddInFlightRef.current = false;
      }
    };

    window.addEventListener('bz:watchlist:add', handler);
    (window as unknown as { bzWatchlistReady?: boolean }).bzWatchlistReady = true;

    return () => {
      window.removeEventListener('bz:watchlist:add', handler);
      (window as unknown as { bzWatchlistReady?: boolean }).bzWatchlistReady = false;
    };
  }, [session]);

  return null;
}

export default RegisterEffects;
